/**
 * Dashboard & Platform Navigation Documentation
 */
import type { DocPage } from '../types';

export const dashboardDocs: DocPage[] = [
  {
    id: 'dashboard/overview',
    title: 'Quanthop Platform Overview',
    category: 'dashboard',
    tags: ['dashboard', 'overview', 'navigation', 'ui', 'workspace'],
    version: '2.0',
    related: ['dashboard/research-lab', 'dashboard/projects'],
    order: 1,
    description: 'Navigating the Quanthop platform and understanding the interface',
    content: `# Quanthop Platform Overview

Quanthop is a strategy research platform built around a single principle: **strategies should be validated before they are trusted.**

When you sign in, you land in the **Research Lab** — a unified workspace for writing, testing, and validating trading strategies.

## Main Navigation

The platform is organized into these areas:

| Area | Purpose |
|------|---------|
| **Research Lab** | Strategy development, backtesting, optimization, and analysis |
| **WFA Results** | Saved Walk-Forward Analysis results and comparisons |
| **Adaptive Flow** | Post-deployment monitoring and reoptimization |
| **Profile** | Account settings and preferences |
| **Billing** | Subscription management and invoices |

## Research Lab Layout

The Research Lab is an IDE-style interface with three panels:

- **Left sidebar** — Project and workspace navigation
- **Main editor** — Strategy code editor (always accessible)
- **Right dock** — Context-sensitive panels that change based on your current task

## Right Dock Tabs

The right dock contains the tools for each stage of the research workflow:

| Tab | What It Does |
|-----|--------------|
| **Definition** | Edit your QSL strategy code |
| **Backtest** | [Configure and run single backtests](/docs/backtesting/running-a-backtest) |
| **Optimization** | [Search parameter space for stable regions](/docs/strategy-optimization/overview) |
| **WFA** | [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) configuration |
| **Robustness** | [Stability scoring](/docs/concepts/parameter-stability) and cluster analysis |
| **Results** | [Browse and compare](/docs/backtesting/comparing-runs) backtest results |
| **Intelligence** | AI-assisted strategy analysis |

## Workflow

The typical workflow follows four stages:

1. **Define** — Write your strategy in QSL
2. **Validate** — Backtest across different conditions
3. **Stabilize** — Find robust parameter regions via optimization and WFA
4. **Deploy** — Monitor with Adaptive Flow

Each stage has dedicated tooling in the Research Lab. You do not need to leave the workspace to complete the full pipeline.

## Next Steps

- [Research Lab](/docs/dashboard/research-lab) — Detailed guide to the IDE workspace
- [Projects](/docs/dashboard/projects) — Creating and managing strategy projects
- [Getting Started](/docs/getting-started/overview) — Introduction to QSL strategies
`,
  },
  {
    id: 'dashboard/research-lab',
    title: 'Research Lab',
    category: 'dashboard',
    tags: ['research lab', 'ide', 'workspace', 'editor', 'tabs'],
    version: '2.0',
    related: ['dashboard/overview', 'dashboard/projects', 'backtesting/running-a-backtest'],
    order: 2,
    description: 'Using the Research Lab IDE for strategy development and validation',
    content: `# Research Lab

The Research Lab is the main workspace. It combines a code editor, backtest runner, optimizer, and analysis tools into a single interface.

## Code Editor

The left panel contains a full-featured code editor for your QSL strategy:

- Syntax highlighting for JavaScript/QSL
- Parameter extraction from your \`define()\` function
- Inline error reporting when strategies fail validation

Changes to the code are reflected immediately — save your strategy, and the parameter panel updates to match.

## Running a Backtest

From the **Backtest** tab in the right dock:

1. Select a symbol (e.g., BTCUSDT) and interval (e.g., 4h)
2. Set the date range or candle range
3. Configure portfolio amount and trading fees
4. Click **Run Backtest**

Results appear in the same panel: equity curve, trade list, and performance metrics.

## Switching Contexts

The right dock tabs let you move through the research pipeline without leaving the page:

- Write code in **Definition**
- Test it in [**Backtest**](/docs/backtesting/running-a-backtest)
- Search for robust parameters in [**Optimization**](/docs/strategy-optimization/running-optimization)
- Validate with [**WFA**](/docs/strategy-optimization/walk-forward-analysis)
- Analyze stability in [**Robustness**](/docs/concepts/parameter-stability)

Your strategy code stays mounted across all tabs. Switching between them does not reset your work.

## Keyboard Shortcuts

| Shortcut | Action |
|----------|--------|
| \`Ctrl+S\` | Save strategy |
| \`Ctrl+Enter\` | Run backtest |

## Next Steps

- [Running a Backtest](/docs/backtesting/running-a-backtest) — Detailed backtest guide
- [Running Optimization](/docs/strategy-optimization/running-optimization) — Parameter search guide
- [Projects](/docs/dashboard/projects) — Managing strategy projects
`,
  },
  {
    id: 'dashboard/projects',
    title: 'Projects',
    category: 'dashboard',
    tags: ['projects', 'workspace', 'strategy management', 'create', 'organize'],
    version: '2.0',
    related: ['dashboard/research-lab', 'getting-started/overview'],
    order: 3,
    description: 'Creating, managing, and organizing strategy projects',
    content: `# Projects

Projects are how you organize your strategies in Quanthop. Each project contains a QSL strategy with its parameters, backtest history, and optimization results.

## Creating a Project

When you first open the Research Lab with no active project, you will see the project browser. From here you can:

- **Create a new project** — Start with a blank strategy or choose from a template
- **Open an existing project** — Resume work on a saved strategy

## Strategy Templates

Templates give you a working starting point. Available templates include:

| Template | Description |
|----------|-------------|
| **Blank** | Empty strategy with the required function stubs |
| **EMA Cross** | Simple exponential moving average crossover |
| **RSI Mean Reversion** | RSI-based mean reversion strategy |
| **Bollinger Breakout** | Bollinger Band breakout system |
| **MACD Momentum** | MACD-based momentum strategy |
| **Golden Cross** | Long-term moving average crossover |
| **TDR Advanced** | Multi-indicator strategy with risk engine |

## Managing Strategies

Each strategy is stored with:

- The QSL code
- Parameter definitions from \`define()\`
- Version history
- Associated backtest results

You can rename, duplicate, or delete strategies from the project browser.

## Next Steps

- [Minimal Strategy](/docs/examples/minimal-strategy) — Your first QSL strategy
- [Strategy Lifecycle](/docs/getting-started/strategy-lifecycle) — How strategies execute
`,
  },
];
